import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { useAuth } from '../../../hooks/auth';
import { useToast } from '../../../hooks/toast';
import {
  getListProviderSubjects,
  getListSellerSubject,
  postAddProvidersCourseItem,
  postAddSellersCourseItem,
} from '../../../services/api';
import getValidationErrors from '../../../utils/getVaidationErrors';
import Buttom from '../../forms/Buttom';
import Checkbox from '../../forms/Checkbox';
import InputSearch from '../../forms/InputSearch';
import Modal from '../Modal';
import {
  Container,
  ContainerFlex,
  Bottom,
  Both,
  Subject,
  SubjectContent,
  SubjectOptions,
  EmptyTitle,
} from './styles';

interface IModalProps {
  isOpen: boolean;
  setIsOpen: () => void;
  clearFilter: () => void;
  courseId: string;
}
export interface ISubjects {
  subject_id: string;
  name: string;
}

const ModalAddSubjectToCourse: React.FC<IModalProps> = ({
  isOpen,
  setIsOpen,
  courseId,
  clearFilter,
}) => {
  const { user } = useAuth();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const formRef = useRef<FormHandles>(null);
  const [subjects, setSubjects] = useState<ISubjects[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<ISubjects[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        if (user.seller) {
          const { data: res } = await getListSellerSubject({
            sellerId: user.seller.id,
          });
          setSubjects(res);
          setFilteredSubjects(res);
        } else if (user.type === 'provider' && user.provider) {
          const { data: res } = await getListProviderSubjects(user.provider.id);
          setSubjects(res);
          setFilteredSubjects(res);
        }
      } catch (error) {
        addToast({
          title: 'Ops...',
          type: 'error',
          description: 'Algo inesperado aconteceu.',
        });
      }
    };
    load();
  }, [addToast, user.seller, user.type, user.provider]);

  // Filtro de busca
  useEffect(() => {
    if (search) {
      const filtered = subjects.filter(subject =>
        subject.name.toLowerCase().includes(search.toLowerCase()),
      );
      setFilteredSubjects(filtered);
    } else {
      setFilteredSubjects(subjects);
    }
  }, [search, subjects]);

  const handleSubmit = useCallback(
    async data => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        // Adiciona todas as disciplinas selecionadas
        const promises = selectedSubjects
          .map(subjectId => {
            if (
              user.type === 'seller' ||
              user.type === 'seller_content_manager'
            ) {
              return postAddSellersCourseItem(subjectId, courseId);
            }
            if (user.type === 'provider') {
              return postAddProvidersCourseItem(subjectId, courseId);
            }
            return null;
          })
          .filter(Boolean);

        await Promise.all(promises);

        addToast({
          title: 'Disciplinas cadastradas com sucesso!',
          type: 'success',
        });
        clearFilter();
        setIsOpen();
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          formRef.current?.setErrors(errors);
          return;
        }
        addToast({
          title: 'Ops...',
          type: 'error',
          description: 'Algo inesperado aconteceu.',
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, clearFilter, courseId, setIsOpen, selectedSubjects, user.type],
  );

  const handleCheck = useCallback((check: string[]) => {
    setSelectedSubjects(check);
  }, []);

  return (
    <Modal isOpen={isOpen} setIsOpen={setIsOpen} overflow="hidden">
      <Container>
        <h1>Adicionar disciplinas ao curso</h1>
        <ContainerFlex>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <div style={{ margin: '40px 0', height: 'auto' }}>
              {subjects.length > 0 && (
                <InputSearch
                  name="search"
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Pesquisar disciplinas"
                  value={search}
                />
              )}
              {filteredSubjects.length > 0 ? (
                filteredSubjects.map((item, i) => (
                  <Subject key={item.subject_id} className={`subject-${i}`}>
                    <SubjectContent>
                      <h3>{item.name}</h3>
                    </SubjectContent>
                    <SubjectOptions>
                      <Checkbox
                        name="check"
                        handleCheck={handleCheck}
                        checks={selectedSubjects}
                        id={item.subject_id}
                      />
                    </SubjectOptions>
                  </Subject>
                ))
              ) : (
                <EmptyTitle>
                  {search
                    ? 'Nenhuma disciplina encontrada...'
                    : 'Não há disciplinas disponíveis...'}
                </EmptyTitle>
              )}
            </div>

            <Both />
            <button type="submit" hidden>
              submeter
            </button>
          </Form>
        </ContainerFlex>
        <Bottom>
          <Buttom
            name="cancelar"
            type="button"
            onClick={() => setIsOpen()}
            label="Cancelar"
            outline
          />
          <Buttom
            name="salvar"
            onClick={() => formRef.current?.submitForm()}
            label="Salvar"
            isLoading={loading}
            disabled={selectedSubjects.length === 0}
          />
        </Bottom>
      </Container>
    </Modal>
  );
};

export default ModalAddSubjectToCourse;
